import React, { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { CheckCircle2, ArrowRight, LayoutTemplate, Target, Workflow, Briefcase, ChevronRight, Check, CheckSquare } from 'lucide-react';

export const Proposta: React.FC<{ slug: string }> = ({ slug }) => {
  // We only have one proposal now, but we can accept the slug for future expansion
  const isCiaDoPlastico = slug === 'cia-do-plastico';

  const [activeSection, setActiveSection] = useState('introducao');

  const scrollTo = (id: string) => {
    setActiveSection(id);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  useEffect(() => {
    const handleScroll = () => {
      const sections = ['introducao', 'objetivos', 'fases', 'investimento', 'checklist'];
      for (const section of sections) {
        const el = document.getElementById(section);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 100 && rect.bottom >= 100) {
            setActiveSection(section);
            break;
          }
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (!isCiaDoPlastico) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 text-gray-500">
        Proposta não encontrada para {slug}
      </div>
    );
  }

  return (
    <div className="bg-white min-h-screen text-slate-800 font-sans flex flex-col md:flex-row">
      {/* Sidebar / Menu */}
      <nav className="w-full md:w-64 border-r border-slate-200 bg-slate-50/50 p-6 md:h-screen md:sticky top-0 overflow-y-auto hidden md:block shrink-0">
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-8">
            <div className="w-8 h-8 bg-zinc-900 rounded-lg flex items-center justify-center text-white font-bold text-sm">
              N
            </div>
            <span className="font-bold text-2xl tracking-tight text-slate-900">NYROH.</span>
          </div>
          <h1 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1">Proposta Comercial</h1>
          <h2 className="text-xl font-semibold text-slate-900 tracking-tight">Cia dos Plásticos</h2>
        </div>

        <ul className="space-y-1">
          {[
            { id: 'introducao', label: '1. Introdução', icon: LayoutTemplate },
            { id: 'objetivos', label: '2. Objetivos', icon: Target },
            { id: 'fases', label: '3. Fases do Projeto', icon: Workflow },
            { id: 'investimento', label: '4. Investimento', icon: Briefcase },
            { id: 'checklist', label: '5. Checklist de Início', icon: CheckCircle2 }
          ].map((item) => {
            const Icon = item.icon;
            const isActive = activeSection === item.id;
            return (
              <li key={item.id}>
                <button
                  onClick={() => scrollTo(item.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                    isActive 
                      ? 'bg-indigo-50 text-indigo-700' 
                      : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                  }`}
                >
                  <Icon size={16} className={isActive ? 'text-indigo-600' : 'text-slate-400'} />
                  {item.label}
                </button>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0">
        
        {/* Mobile Sticky Nav with NYROH Branding */}
        <div className="md:hidden sticky top-0 z-50 bg-white/90 backdrop-blur-xl border-b border-slate-100 px-6 py-4 flex items-center justify-between shadow-sm">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-zinc-900 rounded-lg flex items-center justify-center text-white font-bold text-sm">
              N
            </div>
            <span className="font-bold tracking-tight text-slate-900 text-lg">NYROH.</span>
          </div>
        </div>

        <main className="flex-1 bg-white p-6 pt-8 md:p-16 lg:p-24 lg:max-w-4xl mx-auto w-full">
        
        {/* Mobile Header (hidden on large screens) */}
        <div className="md:hidden mb-12">
           <div className="inline-block px-3 py-1 bg-indigo-50 text-indigo-700 text-[10px] font-bold uppercase tracking-widest rounded-full mb-4">Proposta Comercial</div>
           <h2 className="text-3xl font-bold text-slate-900 tracking-tight leading-tight">Companhia dos Plásticos</h2>
        </div>

        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
           
          {/* 1. Introducao */}
          <section id="introducao" className="mb-20 scroll-mt-24">
            <h3 className="text-2xl font-semibold text-slate-900 mb-6 tracking-tight">1. Introdução</h3>
            <p className="text-slate-600 leading-relaxed mb-4 text-[17px]">
              A <strong>Companhia dos Plásticos</strong> já possui uma operação consolidada no mercado físico e digital, com histórico de vendas, presença ativa em canais digitais e estrutura funcional de e-commerce.
            </p>
            <p className="text-slate-600 leading-relaxed mb-4 text-[17px]">
              No entanto, o crescimento digital atual ainda acontece de forma fragmentada, com dependência de ações isoladas de tráfego, conteúdo e atendimento, sem um sistema integrado de aquisição, conversão e escala.
            </p>
            <p className="text-slate-600 leading-relaxed text-[17px]">
              O objetivo deste projeto é <strong>estruturar um modelo completo de crescimento digital</strong>, conectando estratégia, operação e execução em um único sistema, com capacidade de ser operado internamente após a implantação.
            </p>
          </section>

          {/* 2. Objetivos */}
          <section id="objetivos" className="mb-20 scroll-mt-24">
            <h3 className="text-2xl font-semibold text-slate-900 mb-6 tracking-tight">2. Objetivo do Projeto</h3>
            <p className="text-slate-600 mb-6 text-[17px]">Criar uma operação digital estruturada e previsível, capaz de:</p>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                "aumentar vendas online de forma consistente",
                "estruturar tráfego pago de forma eficiente (Google e Meta)",
                "melhorar conversão do e-commerce e WhatsApp",
                "fortalecer presença digital nacional",
                "formar liderança interna de marketing",
                "reduzir dependência de terceiros ao longo do tempo"
              ].map((item, idx) => (
                <div key={idx} className="flex items-start gap-3 bg-slate-50 p-4 rounded-xl border border-slate-100">
                  <div className="mt-0.5 bg-indigo-100 p-1 rounded-full text-indigo-600 shrink-0">
                    <Check size={14} strokeWidth={3} />
                  </div>
                  <span className="text-slate-700 font-medium text-sm leading-snug">{item}</span>
                </div>
              ))}
            </div>
          </section>

          {/* 3. Fases */}
          <section id="fases" className="mb-20 scroll-mt-24">
            <h3 className="text-2xl font-semibold text-slate-900 mb-6 tracking-tight">3. Modelo Geral</h3>
            <p className="text-slate-600 mb-4 text-[17px]">O projeto será executado em três etapas contínuas:</p>
            <ul className="text-slate-600 mb-4 text-[17px] list-disc list-inside">
              <li>Fase 1: Implantação</li>
              <li>Fase 2: Otimização e Transição</li>
              <li>Fase 3: Continuidade opcional</li>
            </ul>
            <p className="text-slate-600 mb-8 text-[17px]">Além disso, um sistema operacional será construído desde o início para garantir consistência e autonomia da operação.</p>

            <div className="space-y-6">
              {/* Fase 1 */}
              <div className="border border-slate-200 rounded-2xl p-6 md:p-8 bg-white relative overflow-hidden group">
                <div className="absolute top-0 left-0 w-1 h-full bg-indigo-500"></div>
                <div className="flex items-center gap-3 mb-6">
                  <span className="bg-indigo-100 text-indigo-700 text-xs font-bold px-2 py-1 rounded uppercase tracking-wider">Mês 1</span>
                  <h4 className="text-xl font-bold text-slate-900 uppercase">Fase 1 — Implantação (Mês 1)</h4>
                </div>
                <p className="text-slate-500 mb-6 text-sm">Nesta fase, será construída toda a base da operação digital.</p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6 text-sm">
                  <div>
                    <strong className="text-slate-900 block mb-2 font-semibold">Estruturação técnica:</strong>
                    <ul className="space-y-1.5 text-slate-600 list-disc list-inside marker:text-indigo-300">
                      <li>Google Shopping</li>
                      <li>Google Ads (Search)</li>
                      <li>Meta Ads</li>
                      <li>configuração de rastreamento e métricas</li>
                      <li>organização de catálogo e produtos</li>
                      <li>estrutura inicial de campanhas</li>
                    </ul>
                  </div>
                  <div>
                    <strong className="text-slate-900 block mb-2 font-semibold">Estrutura de operação:</strong>
                    <ul className="space-y-1.5 text-slate-600 list-disc list-inside marker:text-indigo-300">
                      <li>organização da jornada de compra (site + WhatsApp)</li>
                      <li>definição da rotina de conteúdo</li>
                      <li>estruturação de criativos iniciais</li>
                      <li>organização do fluxo de vendas</li>
                    </ul>
                  </div>
                  <div className="md:col-span-2">
                    <strong className="text-slate-900 block mb-2 font-semibold">Treinamento interno:</strong>
                    <ul className="space-y-1.5 text-slate-600 list-disc list-inside marker:text-indigo-300">
                      <li>início do treinamento da responsável interna (filha)</li>
                      <li>introdução ao modelo de operação digital</li>
                      <li>entendimento de campanhas e decisões básicas</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Fase 2 */}
              <div className="border border-slate-200 rounded-2xl p-6 md:p-8 bg-white relative overflow-hidden group">
                <div className="absolute top-0 left-0 w-1 h-full bg-emerald-500"></div>
                <div className="flex items-center gap-3 mb-6">
                  <span className="bg-emerald-100 text-emerald-700 text-xs font-bold px-2 py-1 rounded uppercase tracking-wider">Mês 2</span>
                  <h4 className="text-xl font-bold text-slate-900 uppercase">Fase 2 — Otimização e Transição (Mês 2)</h4>
                </div>
                <p className="text-slate-500 mb-6 text-sm">Nesta fase, a operação começa a ser consolidada e transferida.</p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6 text-sm">
                  <div>
                    <strong className="text-slate-900 block mb-2 font-semibold">Otimização:</strong>
                    <ul className="space-y-1.5 text-slate-600 list-disc list-inside marker:text-emerald-300">
                      <li>ajuste de campanhas ativas</li>
                      <li>testes de criativos</li>
                      <li>análise de performance</li>
                      <li>melhoria de conversão</li>
                    </ul>
                  </div>
                  <div>
                    <strong className="text-slate-900 block mb-2 font-semibold">Transição operacional:</strong>
                    <ul className="space-y-1.5 text-slate-600 list-disc list-inside marker:text-emerald-300">
                      <li>responsável interna assume liderança do marketing</li>
                      <li>execução passa a ser interna</li>
                      <li>rotina operacional é estabilizada</li>
                      <li>decisões começam a ser tomadas internamente</li>
                    </ul>
                  </div>
                  <div className="md:col-span-2">
                    <strong className="text-slate-900 block mb-2 font-semibold">Objetivo da fase:</strong>
                    <ul className="space-y-1.5 text-slate-600 list-disc list-inside marker:text-emerald-300">
                      <li>garantir operação funcionando de forma independente</li>
                      <li>estabilizar processo de crescimento</li>
                      <li>preparar estrutura para escala</li>
                    </ul>
                  </div>
                </div>
                
                <div className="mt-8 pt-6 border-t border-slate-100">
                  <strong className="text-slate-900 block mb-2 font-semibold uppercase text-xs tracking-widest">Encerramento da Fase 2</strong>
                  <p className="text-slate-600 text-sm mb-3">Ao final do segundo mês:</p>
                  <ul className="space-y-1.5 text-slate-600 list-disc list-inside marker:text-slate-300 text-sm mb-4">
                    <li>operação estruturada e funcional</li>
                    <li>equipe interna liderando execução</li>
                    <li>sistema operacional implementado</li>
                    <li>empresa pronta para continuidade e escala</li>
                  </ul>
                </div>
              </div>

              {/* Sistema Operacional Opcional callout */}
              <div className="bg-slate-900 text-white rounded-2xl p-6 md:p-8 relative overflow-hidden">
                <div className="absolute top-0 right-0 p-8 opacity-10 pointer-events-none">
                  <Workflow size={120} />
                </div>
                <h4 className="text-lg font-bold mb-2 uppercase tracking-wide">Opcional: Sistema Operacional</h4>
                <ul className="space-y-2 text-sm text-slate-300 mt-4">
                  <li className="flex items-center gap-2"><Check size={14} className="text-indigo-400" /> início da construção do sistema de operação (leve e prático)</li>
                  <li className="flex items-center gap-2"><Check size={14} className="text-indigo-400" /> estruturação da lógica de execução e rotina</li>
                </ul>
              </div>

              {/* Fase 3 */}
              <div className="border border-slate-200 rounded-2xl p-6 md:p-8 bg-white relative overflow-hidden">
                 <div className="absolute top-0 left-0 w-1 h-full bg-slate-300"></div>
                 <h4 className="text-xl font-bold text-slate-900 mb-2 uppercase">Fase 3 — Continuidade Opcional</h4>
                 <p className="text-slate-500 mb-6 text-sm">Após a implantação, a empresa pode optar por continuar com suporte estratégico.</p>
                 
                 <div className="space-y-6">
                    <div className="bg-slate-50 p-5 rounded-xl border border-slate-100">
                      <div className="flex justify-between items-start mb-2">
                        <strong className="text-slate-900 font-semibold block uppercase text-sm tracking-wide">3.1 Acompanhamento e Direção (R$ 1.300/mês)</strong>
                      </div>
                      <p className="text-slate-600 text-sm mb-3">Nesta modalidade, o foco é estratégico.</p>
                      
                      <strong className="text-slate-900 block mb-2 font-semibold text-sm">Inclui:</strong>
                      <ul className="space-y-1.5 text-slate-600 list-disc list-inside text-sm mb-4">
                        <li>acompanhamento da performance</li>
                        <li>análise de campanhas e resultados</li>
                        <li>orientação em decisões de marketing</li>
                        <li>suporte na interpretação de métricas</li>
                        <li>ajustes de direção de crescimento</li>
                      </ul>

                      <strong className="text-slate-900 block mb-2 font-semibold text-sm">Estrutura:</strong>
                      <ul className="space-y-1.5 text-slate-600 list-disc list-inside text-sm">
                        <li>equipe interna executa</li>
                        <li>liderança interna conduz operação</li>
                        <li>suporte externo atua como direção estratégica</li>
                      </ul>
                    </div>

                    <div className="bg-slate-50 p-5 rounded-xl border border-slate-100">
                      <strong className="text-slate-900 font-semibold block mb-2 uppercase text-sm tracking-wide">3.2 Suporte com Equipe Operacional (Opcional)</strong>
                      <p className="text-slate-600 text-sm mb-3">Caso a empresa deseje reforço na execução, pode ser incluída uma equipe especializada.</p>
                      
                      <strong className="text-slate-900 block mb-2 font-semibold text-sm">Estrutura:</strong>
                      <ul className="space-y-1.5 text-slate-600 list-disc list-inside text-sm mb-4">
                        <li>profissionais de tráfego (Google / Meta)</li>
                        <li>suporte em execução técnica</li>
                        <li>freelancers especializados em operação digital</li>
                      </ul>

                      <strong className="text-slate-900 block mb-2 font-semibold text-sm">Modelo:</strong>
                      <ul className="space-y-1.5 text-slate-600 list-disc list-inside text-sm">
                        <li>empresa contrata execução separadamente</li>
                        <li>direção estratégica permanece centralizada</li>
                        <li>operação se torna mais robusta e escalável</li>
                      </ul>
                    </div>

                    <div className="bg-slate-50 p-5 rounded-xl border border-slate-100">
                      <strong className="text-slate-900 font-semibold block mb-2 uppercase text-sm tracking-wide">3.3 Sistema Operacional (Iniciado na Fase 1)</strong>
                      <p className="text-slate-600 text-sm mb-3">O sistema não é uma fase isolada, mas um componente contínuo do projeto.<br/>Ele começa na implantação e evolui ao longo das fases.</p>
                      
                      <strong className="text-slate-900 block mb-2 font-semibold text-sm">Função:</strong>
                      <ul className="space-y-1.5 text-slate-600 list-disc list-inside text-sm">
                        <li>organizar rotina de marketing</li>
                        <li>estruturar decisões operacionais</li>
                        <li>garantir consistência da execução</li>
                        <li>apoiar autonomia da equipe interna</li>
                        <li>sustentar crescimento após saída da implantação</li>
                      </ul>
                    </div>
                 </div>
              </div>

            </div>
          </section>

          {/* 4. Investimento */}
          <section id="investimento" className="mb-20 scroll-mt-24">
            <h3 className="text-2xl font-semibold text-slate-900 mb-6 tracking-tight uppercase">4. Investimento</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="border border-indigo-100 bg-indigo-50/50 rounded-2xl p-6 shadow-sm relative overflow-hidden">
                <h4 className="text-lg font-bold text-slate-900 mb-2">Implantação (2 meses)</h4>
                <div className="text-3xl font-bold text-indigo-700 mb-4">R$ 10.000</div>
                <ul className="space-y-2 text-sm text-slate-700">
                  <li className="flex justify-between border-b border-indigo-100 pb-2"><span>início</span> <strong>R$ 5.000</strong></li>
                  <li className="flex justify-between pt-1"><span>final do segundo mês</span> <strong>R$ 5.000</strong></li>
                </ul>
              </div>

              <div className="border border-emerald-100 bg-emerald-50/50 rounded-2xl p-6 shadow-sm relative overflow-hidden">
                <h4 className="text-lg font-bold text-slate-900 mb-2 uppercase">SISTEMA</h4>
                <div className="text-3xl font-bold text-emerald-700 mb-4">R$ 7.500</div>
                <ul className="space-y-2 text-sm text-slate-700">
                  <li className="flex justify-between border-b border-emerald-100 pb-2"><span>inicio</span> <strong>R$ 3.750</strong></li>
                  <li className="flex justify-between pt-1"><span>finalizado</span> <strong>R$ 3.750</strong></li>
                </ul>
              </div>
            </div>

            <div className="mt-6 border border-slate-200 rounded-xl p-5 bg-white text-sm">
               <h4 className="font-bold text-slate-900 mb-3">Continuidade opcional:</h4>
               <ul className="space-y-2 text-slate-600">
                 <li className="flex justify-between items-center bg-slate-50 p-2 rounded"><span>Acompanhamento estratégico:</span> <strong className="text-slate-900">R$ 1.300/mês</strong></li>
                 <li className="flex justify-between items-center bg-slate-50 p-2 rounded"><span>Estrutura com equipe operacional:</span> <strong className="text-slate-900">sob demanda</strong></li>
               </ul>
            </div>
          </section>

          {/* 5. Inicio */}
          <section className="mb-20">
            <h3 className="text-2xl font-semibold text-slate-900 mb-4 tracking-tight uppercase">5. Início do Projeto</h3>
            <p className="text-slate-600 mb-4 text-[17px]">Após aprovação:</p>
            <ul className="space-y-1.5 text-slate-600 list-disc list-inside text-[17px] mb-4">
              <li>coleta de informações iniciais da operação</li>
              <li>acesso às estruturas atuais (site, redes, anúncios)</li>
              <li>início da implantação da estratégia</li>
              <li>alinhamento da rotina com a equipe interna</li>
            </ul>
          </section>

          {/* 6. Fechamento */}
          <section className="mb-20">
            <h3 className="text-2xl font-semibold text-slate-900 mb-4 tracking-tight uppercase">6. Fechamento</h3>
            <p className="text-slate-600 text-[17px] leading-relaxed">
              Este projeto tem como objetivo não apenas estruturar marketing, mas construir uma operação digital funcional, independente e escalável, com capacidade interna de gestão e crescimento contínuo
            </p>
          </section>

          {/* Checklist */}
          <section id="checklist" className="mb-20 scroll-mt-24 pt-10 border-t border-slate-200">
             <h3 className="text-2xl font-semibold text-slate-900 mb-2 tracking-tight flex items-center gap-2">
                <CheckSquare size={24} className="text-indigo-600" /> Checklist de Início
             </h3>
             <p className="text-slate-500 mb-8 text-sm">Informações necessárias para o kick-off do projeto.</p>

             <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-10">
                
                {[
                  {
                    title: "1. ESTRUTURA DO NEGÓCIO",
                    items: [
                      "Produtos principais vendidos",
                      "Produtos de maior margem",
                      "Produtos de maior volume de venda",
                      "Produtos sazonais",
                      "Produtos estratégicos (para escalar)",
                      "Ticket médio atual",
                      "Capacidade de estoque",
                      "Capacidade de reposição"
                    ]
                  },
                  {
                    title: "2. CLIENTE E MERCADO",
                    items: [
                      "Perfil do cliente atual (quem compra hoje)",
                      "Principais tipos de compradores (atacado / varejo)",
                      "Região de maior venda",
                      "Principais dúvidas dos clientes",
                      "Principais objeções na compra",
                      "Produtos mais buscados pelos clientes"
                    ]
                  },
                  {
                    title: "3. OPERAÇÃO DE VENDAS",
                    items: [
                      "Como funciona o processo de venda hoje",
                      "Canais de venda ativos (loja, WhatsApp, Instagram etc.)",
                      "Tempo médio de resposta no atendimento",
                      "Processo de fechamento de pedido",
                      "Processo de pagamento",
                      "Processo de entrega/logística"
                    ]
                  },
                  {
                    title: "4. E-COMMERCE E SITE",
                    items: [
                      "Plataforma utilizada",
                      "Produtos cadastrados atualmente",
                      "Organização do catálogo",
                      "Fotos dos produtos (qualidade e padrão atual)",
                      "Taxa de conversão do site (se existir)",
                      "Carrinhos abandonados (se houver dado)"
                    ]
                  },
                  {
                    title: "5. TRÁFEGO PAGO ATUAL",
                    items: [
                      "Contas ativas (Meta Ads / Google Ads)",
                      "Campanhas em andamento",
                      "Orçamento atual de mídia",
                      "Resultados recentes (vendas / leads)",
                      "Criativos utilizados atualmente",
                      "Públicos utilizados",
                      "Histórico de campanhas que deram certo e errado"
                    ]
                  },
                  {
                    title: "6. CONTEÚDO E REDES SOCIAIS",
                    items: [
                      "Acesso ao Instagram e Facebook",
                      "Tipos de conteúdo que já postam",
                      "Conteúdos que performaram melhor",
                      "Frequência de postagem",
                      "Engajamento médio",
                      "Vídeos disponíveis (banco de conteúdo)",
                      "Influenciadores/parcerias existentes"
                    ]
                  },
                  {
                    title: "7. EQUIPE INTERNA",
                    items: [
                      "Quem faz o quê hoje",
                      "Função da filha na operação",
                      "Função de cada colaborador",
                      "Limitações da equipe atual",
                      "Capacidade de execução de conteúdo",
                      "Capacidade de execução de marketing"
                    ]
                  },
                  {
                    title: "8. FERRAMENTAS E ACESSOS",
                    items: [
                      "Google Ads",
                      "Meta Business Manager",
                      "Google Analytics",
                      "Google Merchant Center",
                      "Plataforma do site/e-commerce",
                      "WhatsApp Business",
                      "CRM (se existir)"
                    ]
                  },
                  {
                    title: "9. FINANCEIRO (IMPORTANTE)",
                    items: [
                      "Faturamento médio mensal",
                      "Margem média por produto",
                      "Produtos mais lucrativos",
                      "CAC (se existir)",
                      "Ticket médio",
                      "Capacidade de investimento em tráfego"
                    ]
                  },
                  {
                    title: "10. LOGÍSTICA E OPERAÇÃO",
                    items: [
                      "Prazo de entrega médio",
                      "Região de envio",
                      "Custo de frete médio",
                      "Limitações logísticas",
                      "Capacidade de escala de pedidos"
                    ]
                  },
                  {
                    title: "11. HISTÓRICO DE MARKETING",
                    items: [
                      "O que já foi feito com agência",
                      "O que funcionou",
                      "O que não funcionou",
                      "Principais erros anteriores",
                      "Expectativas frustradas"
                    ]
                  },
                  {
                    title: "12. OBJETIVOS DA EMPRESA",
                    items: [
                      "Meta de faturamento",
                      "Meta de crescimento digital",
                      "Prioridade (volume, margem ou escala)",
                      "Expectativa com o projeto"
                    ]
                  }
                ].map((block, i) => (
                  <div key={i}>
                    <h5 className="font-bold text-slate-800 border-b border-slate-200 pb-2 mb-3 text-sm">{block.title}</h5>
                    <ul className="space-y-2">
                       {block.items.map((item, j) => (
                         <li key={j} className="flex items-start gap-2 text-sm text-slate-600">
                           <div className="w-4 h-4 rounded border border-slate-300 mt-0.5 shrink-0 bg-slate-50"></div>
                           <span>{item}</span>
                         </li>
                       ))}
                    </ul>
                  </div>
                ))}
             </div>
          </section>

        </motion.div>
      </main>
      </div>
    </div>
  );
};